import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { EmployeeStatus, RequestProps, Technology, User } from '../../store/types'
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  ListItem,
  MenuItem,
  Rating,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CollapsibleSection from '../Button/CollapsibleSection'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import { t } from 'i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { isEqual } from 'lodash'
import FilterMenu from '../FilterMenu/FilterMenu'
import RoleType from '../../store/types/enums/RoleType'
import { useGetTechnologiesQuery } from '../../store/redux/reducers/technologiesSlice'
import TextField from '@mui/material/TextField'
import TechnologyLogo from '../Logo/TechnologyLogo'
import { Circle, RadioButtonUnchecked } from '@mui/icons-material'
import { Box } from '@mui/system'
import { useLeftPadding } from '../../store/redux/hooks'

type FilterUsersMenuProps = {
  requestProps: RequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  setPage: Dispatch<SetStateAction<number>>
}

const FilterUsersMenu = ({ requestProps, setRequestProps, setPage }: FilterUsersMenuProps) => {
  const leftPadding = useLeftPadding()
  const [statuses, setStatuses] = useState<EmployeeStatus[]>([])
  const [isStudent, setIsStudent] = useState<boolean>(false)
  const [selectedLeaders, setSelectedLeaders] = useState<string[]>([])
  const [selectedTechnologies, setSelectedTechnologies] = useState<Technology[]>([])
  const [selectedLevel, setSelectedLevel] = useState<number>(1)
  const defaultTechnologyTypeFilter = 'equalOrGreater'
  const [technologyTypeFilter, setTechnologyTypeFilter] = useState<string>(defaultTechnologyTypeFilter)

  const [displayedLeaders, setDisplayedLeaders] = useState<User[]>([])
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [leaderProps, setLeaderProps] = useState<RequestProps>({
    page: 0,
    size: 5,
    'roles.name': RoleType.Leader,
    employeeStatus: 'ACTIVE',
  })

  const { data: leaders } = useGetLightUsersQuery(leaderProps)
  const { data: technologies, isLoading } = useGetTechnologiesQuery({ page: 0, size: 500 })

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as EmployeeStatus
    const isChecked = e.target.checked

    setStatuses(prevStatuses =>
      isChecked ? [...prevStatuses, value] : prevStatuses.filter(status => status !== value)
    )
  }

  const handleLeaderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const isChecked = e.target.checked

    setSelectedLeaders(prevLeaders =>
      isChecked ? [...prevLeaders, value] : prevLeaders.filter(leader => leader !== value)
    )
  }

  const handleFiltering = () => {
    let newProps: RequestProps = { ...requestProps, page: 0 }

    if (selectedLeaders.length !== 0) {
      newProps = { ...newProps, 'leaders.id': selectedLeaders.toString() }
    } else {
      newProps = { ...newProps, 'leaders.id': undefined }
    }

    if (statuses.length !== 0) {
      newProps = { ...newProps, employeeStatus: statuses.toString() }
    } else {
      newProps = { ...newProps, employeeStatus: undefined }
    }

    if (isStudent) {
      newProps = { ...newProps, student: isStudent }
    } else {
      newProps = { ...newProps, student: undefined }
    }

    if (selectedTechnologies.length !== 0) {
      const levelFilter = technologyTypeFilter === 'equal' ? '' : '>'
      const oppositeLevelFilter = technologyTypeFilter === 'equal' ? '>' : ''
      newProps = {
        ...newProps,
        'technologies.technologyDefinition.id': selectedTechnologies.map(value => value.id).toString(),
        [`technologies.level${levelFilter}`]: selectedLevel.toString(),
        [`technologies.level${oppositeLevelFilter}`]: undefined,
      }
    } else {
      newProps = {
        ...newProps,
        'technologies.technologyDefinition.id': undefined,
        'technologies.level': undefined,
        'technologies.level>': undefined,
      }
    }

    if (isEqual(requestProps, newProps)) return
    setPage(0)
    setRequestProps({ ...newProps })
  }

  const handleClear = () => {
    setStatuses([])
    setIsStudent(false)
    setSelectedLeaders([])
    setSelectedTechnologies([])
    setSelectedLevel(1)
    setTechnologyTypeFilter(defaultTechnologyTypeFilter)
    setPage(0)
    setRequestProps({
      page: 0,
      size: requestProps.size,
      'query:firstName,lastName': requestProps['query:firstName,lastName'],
    })
  }

  const handleFetchMoreData = () => {
    setLeaderProps(prev => ({ ...prev, page: prev.page + 1 }))
  }

  useMemo(() => {
    if (leaders) {
      setHasMore(!leaders.last)
      setDisplayedLeaders(prev => {
        const existingIds = new Set(prev.map(l => l.id))
        const newLeaders = leaders.content.filter((l: User) => !existingIds.has(l.id))
        return [...prev, ...newLeaders]
      })
    }
  }, [leaders])

  useEffect(() => {
    if (leaders && leaders.last) {
      setHasMore(false)
    }
  }, [leaders])

  return (
    <FilterMenu clear={handleClear} filter={handleFiltering}>
      <Stack
        direction="column"
        sx={{
          overflowY: 'auto',
          height: '100%',
          pr: 1,
        }}
      >
        <CollapsibleSection
          text={t('page.users.filtering.status')}
          info={statuses.length !== 0 ? `${t('page.users.filtering.selected')} ${statuses.length}` : ''}
        >
          <FormControlLabel
            sx={{ pl: leftPadding }}
            control={<Checkbox checked={statuses.includes('ACTIVE')} onChange={handleStatusChange} />}
            label={t('page.users.filtering.active')}
            value="ACTIVE"
          />
          <FormControlLabel
            sx={{ pl: leftPadding }}
            control={<Checkbox checked={statuses.includes('BLOCKED')} onChange={handleStatusChange} />}
            label={t('page.users.filtering.blocked')}
            value="BLOCKED"
          />
        </CollapsibleSection>

        <CollapsibleSection text={t('page.users.filtering.studentStatus')}>
          <FormControlLabel
            sx={{ pl: leftPadding }}
            control={<Checkbox checked={isStudent} onChange={e => setIsStudent(e.target.checked)} />}
            label="Student"
          />
        </CollapsibleSection>

        {leaders && (
          <CollapsibleSection
            text={t('page.users.filtering.leader')}
            info={selectedLeaders.length !== 0 ? `${t('page.users.filtering.selected')} ${selectedLeaders.length}` : ''}
          >
            <Box
              sx={{
                height: 150,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              id="scrollableDiv"
            >
              <InfiniteScroll
                dataLength={displayedLeaders.length}
                next={handleFetchMoreData}
                hasMore={hasMore}
                loader={
                  <span>
                    <i>{t('components.infiniteScroll.loading')}</i>
                  </span>
                }
                scrollableTarget="scrollableDiv"
                style={{ overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}
                endMessage={
                  <Typography variant="body2" align="center" paddingY={2}>
                    <i>{t('components.infiniteScroll.endMessage.leaders')}</i>
                  </Typography>
                }
              >
                {displayedLeaders.map((l, _index) => (
                  <Tooltip
                    title={`${l.firstName} ${l.lastName}`}
                    placement="right"
                    key={`${l.id}-filtering-menu-item-tooltip`}
                  >
                    <FormControlLabel
                      sx={{ pl: leftPadding }}
                      control={
                        <Checkbox value={l.id} onChange={handleLeaderChange} checked={selectedLeaders.includes(l.id)} />
                      }
                      label={
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >{`${l.firstName} ${l.lastName}`}</Typography>
                      }
                    />
                  </Tooltip>
                ))}
              </InfiniteScroll>
            </Box>
          </CollapsibleSection>
        )}
        <CollapsibleSection text={t('page.users.filtering.technologyKnowledge')}>
          <Typography variant="caption" sx={{ pt: 1, pb: 1, pl: leftPadding }}>
            {t('page.users.filtering.technologies')}
          </Typography>
          <Autocomplete
            sx={{ pl: leftPadding }}
            multiple
            id="technology-autocomplete"
            size="small"
            disableCloseOnSelect
            loading={isLoading}
            limitTags={3}
            options={technologies ? technologies.content : []}
            value={selectedTechnologies}
            onChange={(_event, value) => {
              setSelectedTechnologies(value)
            }}
            getOptionLabel={option => option.name}
            renderOption={(props, option, { selected }) => {
              const { key, ...otherProps } = props
              return (
                <ListItem key={key} {...otherProps} sx={{ gap: 1.5 }}>
                  <Checkbox sx={{ mx: -1.5 }} checked={selected} />
                  <TechnologyLogo size={24} technology={option} />
                  {option.name}
                </ListItem>
              )
            }}
            renderInput={params => (
              <TextField
                size="small"
                fullWidth
                inputProps={params.inputProps}
                InputProps={params.InputProps}
                label={t('page.users.filtering.technologies')}
              />
            )}
          />
          <Typography sx={{ pt: 2, pb: 1, pl: leftPadding }} variant="caption">
            {t('page.users.filtering.knowledgeLevel')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
              alignItems: 'center',
              justifyContent: 'space-between',
              pl: leftPadding,
            }}
          >
            <Select
              size={'small'}
              sx={{ width: '55%' }}
              defaultValue={defaultTechnologyTypeFilter}
              value={technologyTypeFilter}
              onChange={event => setTechnologyTypeFilter(event.target.value)}
            >
              <MenuItem value={'equalOrGreater'}>{t('page.users.filtering.equalOrGreater')}</MenuItem>
              <MenuItem value={'equal'}>{t('page.users.filtering.equal')}</MenuItem>
            </Select>
            <Rating
              name="customized-color"
              defaultValue={1}
              value={selectedLevel / 2}
              onChange={(_event, newValue) => {
                if (newValue !== null) {
                  setSelectedLevel(newValue * 2)
                }
              }}
              getLabelText={(value: number) => `${value} Heart${value !== 1 ? 's' : ''}`}
              precision={0.5}
              icon={<Circle sx={{ width: 14, mr: 0.25 }} />}
              emptyIcon={<RadioButtonUnchecked sx={{ width: 14, mr: 0.25 }} />}
            />
          </Box>
        </CollapsibleSection>
      </Stack>
    </FilterMenu>
  )
}

export default FilterUsersMenu
